import { Download, Filter, LoaderCircle, Search, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { ProfileTable } from '../components/ProfileTable';
import {
  catalog,
  filterProfiles,
  outputResolutionFilterOptions,
  packsById,
  paramsFromSearchState,
  profilesById,
  searchStateFromParams,
  sortProfiles,
} from '../lib/catalog';
import { downloadProfilesAsZip, selectedProfilesArchiveName } from '../lib/downloads';
import { downloadCountsSnapshot } from '../lib/downloadCounts';
import { navigate } from '../lib/navigation';
import type { SearchState, SortKey } from '../types';

interface SearchPageProps {
  search: string;
  notify: (message: string, tone?: 'success' | 'error') => void;
}

function searchUrl(state: SearchState): string {
  const params = paramsFromSearchState(state);
  return `/search${params.size ? `?${params}` : ''}`;
}

export function SearchPage({ search, notify }: SearchPageProps) {
  const state = searchStateFromParams(new URLSearchParams(search));
  const [query, setQuery] = useState(state.query);
  const [device, setDevice] = useState(state.device);
  const [connection, setConnection] = useState(state.connection);
  const [resolution, setResolution] = useState(state.resolution);
  const [crtEffects, setCrtEffects] = useState(state.crtEffects);
  const [customCrop, setCustomCrop] = useState(state.customCrop);
  const [deepColor, setDeepColor] = useState(state.deepColor);
  const selectionScope = [
    state.query,
    state.device,
    state.connection,
    state.resolution,
    state.crtEffects,
    state.customCrop,
    state.deepColor,
    state.packId,
  ].join('\u0000');
  const [selected, setSelected] = useState<Set<string>>(() => {
    const historyState = window.history.state as { searchSelection?: unknown; searchScope?: unknown } | null;
    return historyState?.searchScope === selectionScope && Array.isArray(historyState.searchSelection)
      ? new Set(historyState.searchSelection.filter((id): id is string => typeof id === 'string' && profilesById.has(id)))
      : new Set();
  });
  const [downloadProgress, setDownloadProgress] = useState<[number, number] | null>(null);
  const previousSelectionScope = useRef(selectionScope);

  useEffect(() => {
    setQuery(state.query);
    setDevice(state.device);
    setConnection(state.connection);
    setResolution(state.resolution);
    setCrtEffects(state.crtEffects);
    setCustomCrop(state.customCrop);
    setDeepColor(state.deepColor);
  }, [search]);

  useEffect(() => {
    if (previousSelectionScope.current === selectionScope) return;
    previousSelectionScope.current = selectionScope;
    setSelected(new Set());
  }, [selectionScope]);

  useEffect(() => {
    window.history.replaceState(
      { ...window.history.state, searchSelection: [...selected], searchScope: selectionScope },
      '',
      window.location.href,
    );
  }, [selected, selectionScope, search]);

  const filtered = useMemo(
    () => filterProfiles(catalog.profiles, state),
    [
      state.query,
      state.device,
      state.connection,
      state.resolution,
      state.crtEffects,
      state.customCrop,
      state.deepColor,
      state.packId,
    ],
  );
  const countRevision = downloadCountsSnapshot();
  const sorted = useMemo(
    () => sortProfiles(filtered, state.sort, state.direction),
    [filtered, state.sort, state.direction, countRevision],
  );
  const pageCount = Math.max(1, Math.ceil(sorted.length / state.pageSize));
  const currentPage = Math.min(state.page, pageCount);
  const pageProfiles = sorted.slice((currentPage - 1) * state.pageSize, currentPage * state.pageSize);
  const activePack = state.packId ? packsById.get(state.packId) : null;
  const filterCount = [
    state.query,
    state.device,
    state.connection,
    state.resolution,
    state.crtEffects,
    state.customCrop,
    state.deepColor,
    state.packId,
  ].filter(Boolean).length;

  function applyFilters(event: FormEvent): void {
    event.preventDefault();
    navigate(searchUrl({
      ...state,
      query: query.trim(),
      device,
      connection,
      resolution,
      crtEffects,
      customCrop,
      deepColor,
      page: 1,
    }));
  }

  function updateSort(sort: SortKey): void {
    const direction = state.sort === sort ? (state.direction === 'asc' ? 'desc' : 'asc') : sort === 'downloads' || sort === 'updatedAt' ? 'desc' : 'asc';
    navigate(searchUrl({ ...state, sort, direction, page: 1 }));
  }

  function toggle(id: string): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(): void {
    setSelected((current) => {
      const allSelected = pageProfiles.every((profile) => current.has(profile.id));
      const next = new Set(current);
      pageProfiles.forEach((profile) => (allSelected ? next.delete(profile.id) : next.add(profile.id)));
      return next;
    });
  }

  async function downloadSelected(): Promise<void> {
    const profiles = [...selected]
      .map((id) => profilesById.get(id))
      .filter((profile): profile is NonNullable<typeof profile> => Boolean(profile));
    if (!profiles.length) return;
    try {
      setDownloadProgress([0, profiles.length]);
      await downloadProfilesAsZip(profiles, selectedProfilesArchiveName(), (completed, total) => setDownloadProgress([completed, total]));
      notify(`${profiles.length} profiles downloaded as a ZIP archive.`);
      setSelected(new Set());
    } catch (error) {
      notify(error instanceof Error ? error.message : 'The selected profiles could not be downloaded.', 'error');
    } finally {
      setDownloadProgress(null);
    }
  }

  return (
    <>
      <div className="page-heading">
        <h1>Browse profiles</h1>
        <p>Search the full community archive, compare decoded metadata, and download one profile or a hand-picked batch.</p>
      </div>

      {activePack && (
        <div className="pack-filter-banner">
          <div><span>Viewing profile pack</span><strong>{activePack.name}</strong><small>{activePack.device} · {activePack.profileCount} profiles</small></div>
          <button className="button ghost" type="button" onClick={() => navigate(searchUrl({ ...state, packId: '', page: 1 }))}><X size={16} />Remove pack filter</button>
        </div>
      )}

      <div className="search-layout">
        <aside className="filter-panel">
          <div className="filter-heading"><h2><SlidersHorizontal size={17} />Filters</h2>{filterCount > 0 && <span>{filterCount} active</span>}</div>
          <form onSubmit={applyFilters}>
            <label>
              <span>Keywords</span>
              <div className="input-with-icon"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, creator, pack…" /></div>
            </label>
            <label>
              <span>Console / Device</span>
              <select value={device} onChange={(event) => setDevice(event.target.value)}>
                <option value="">All devices</option>
                {catalog.devices.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <label>
              <span>Connection Type</span>
              <select value={connection} onChange={(event) => setConnection(event.target.value)}>
                <option value="">All connections</option>
                {catalog.connections.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <label>
              <span>Output Resolution</span>
              <select value={resolution} onChange={(event) => setResolution(event.target.value)}>
                <option value="">All resolutions</option>
                {outputResolutionFilterOptions.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <details className="filter-checkbox-group">
              <summary>Additional Filters</summary>
              <div className="filter-checkbox-options">
                <label className="filter-checkbox">
                  <input type="checkbox" checked={crtEffects} onChange={(event) => setCrtEffects(event.target.checked)} />
                  <span><strong>CRT Effects</strong><small>Scanlines, CRT Sim, or a mask</small></span>
                </label>
                <label className="filter-checkbox">
                  <input type="checkbox" checked={customCrop} onChange={(event) => setCustomCrop(event.target.checked)} />
                  <span><strong>Custom Crop</strong><small>Manual crop values in any input slot</small></span>
                </label>
                <label className="filter-checkbox">
                  <input type="checkbox" checked={deepColor} onChange={(event) => setDeepColor(event.target.checked)} />
                  <span><strong>Deep Color</strong><small>10-bit output enabled</small></span>
                </label>
              </div>
            </details>
            <button className="button primary full-width" type="submit"><Filter size={16} />Apply filters</button>
            {filterCount > 0 && <button className="button ghost full-width" type="button" onClick={() => navigate('/search')}><X size={16} />Clear all</button>}
          </form>
        </aside>

        <section className="results-panel" aria-labelledby="results-heading" aria-busy={Boolean(downloadProgress)}>
          <div className="results-toolbar">
            <div aria-live="polite"><h2 id="results-heading">{sorted.length.toLocaleString()} profile{sorted.length === 1 ? '' : 's'}</h2><p>Page {currentPage} of {pageCount}</p></div>
            <div className="toolbar-actions">
              {selected.size > 0 && (
                <button className="button primary" type="button" onClick={() => void downloadSelected()} disabled={Boolean(downloadProgress)} aria-live="polite">
                  {downloadProgress ? <LoaderCircle className="spin" size={16} /> : <Download size={16} />}
                  {downloadProgress ? `Preparing ${downloadProgress[0]}/${downloadProgress[1]}` : `Download selected (${selected.size})`}
                </button>
              )}
              <label className="inline-select"><span>Rows</span><select value={state.pageSize} onChange={(event) => navigate(searchUrl({ ...state, pageSize: Number(event.target.value), page: 1 }))}><option>25</option><option>50</option><option>100</option></select></label>
            </div>
          </div>

          {pageProfiles.length ? (
            <ProfileTable
              profiles={pageProfiles}
              selected={selected}
              onToggle={toggle}
              onToggleAll={toggleAll}
              sort={state.sort}
              direction={state.direction}
              onSort={updateSort}
              notify={notify}
              showOutputResolution
              returnTo={`/search${search}`}
            />
          ) : (
            <div className="empty-state"><Search size={34} /><h3>No profiles found</h3><p>Try a broader keyword or remove one of the filters.</p><button className="button secondary" type="button" onClick={() => navigate('/search')}>Reset search</button></div>
          )}

          {pageCount > 1 && (
            <nav className="pagination" aria-label="Search result pages">
              <button className="button ghost" type="button" disabled={currentPage <= 1} onClick={() => navigate(searchUrl({ ...state, page: currentPage - 1 }))}>Previous</button>
              <span>{(currentPage - 1) * state.pageSize + 1}–{Math.min(currentPage * state.pageSize, sorted.length)} of {sorted.length.toLocaleString()}</span>
              <button className="button ghost" type="button" disabled={currentPage >= pageCount} onClick={() => navigate(searchUrl({ ...state, page: currentPage + 1 }))}>Next</button>
            </nav>
          )}
        </section>
      </div>
    </>
  );
}
